import { Link } from "react-router-dom";
import { ArrowRight, Compass, FileSignature, Sparkles, Globe2, ShieldCheck, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n/I18nContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const HERO = "https://images.unsplash.com/photo-1694018359679-49465b4c0d61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxSaXlhZGglMjBza3lsaW5lJTIwU2F1ZGklMjBBcmFiaWF8ZW58MHx8fHwxNzg3OTMxNTc5fDA&ixlib=rb-4.1.0&q=85";
const MEETING = "https://images.unsplash.com/photo-1674386491555-5b92161e4d04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxSaXlhZGglMjBza3lsaW5lJTIwU2F1ZGklMjBBcmFiaWF8ZW58MHx8fHwxNzg3OTMxNTc5fDA&ixlib=rb-4.1.0&q=85";

const stepIcons = [Globe2, Sparkles, Compass, FileSignature];

export default function Landing() {
  const { t } = useI18n();
  const steps = t("landing.steps");
  const accents = ["#D4AF37", "#2A9D8F", "#E76F51", "#457B9D"];

  return (
    <div className="min-h-screen bg-bone text-foreground" data-testid="landing-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-bone/70 border-b border-sand">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl font-bold text-midnight tracking-tight">Qafila</span>
            <span className="hidden sm:inline overline text-gold">{t("landing.middleEast")}</span>
          </div>
          <nav className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="rounded-none text-midnight hover:bg-sand/50">{t("landing.login")}</Button>
            </Link>
            <Link to="/register" data-testid="nav-register-link">
              <Button className="rounded-none bg-gold text-midnight hover:brightness-105 hover:-translate-y-0.5 transition-all duration-300">
                {t("landing.register")}
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden mesh-hero">
        <div className="pointer-events-none absolute inset-0 z-0">
          <div className="blob blob-gold animate-float" style={{ top: "-80px", left: "-60px" }} />
          <div className="blob blob-teal animate-float" style={{ bottom: "-100px", right: "6%", animationDelay: "2.5s" }} />
          <div className="blob blob-coral animate-float" style={{ top: "20%", right: "40%", animationDelay: "4s", opacity: 0.35 }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="animate-fade-up">
            <p className="overline text-gold mb-4">{t("landing.heroOverline")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-midnight tracking-tight leading-[1.05]">
              {t("landing.heroTitle1")} <span className="italic">{t("landing.heroTitleEm")}</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-register-btn">
                <Button className="rounded-none h-12 px-7 bg-gold text-midnight hover:brightness-105 hover:-translate-y-0.5 transition-all duration-300">
                  {t("landing.startNow")} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-login-btn">
                <Button variant="outline" className="rounded-none h-12 px-7 border-midnight text-midnight hover:bg-midnight hover:text-bone transition-all duration-300">
                  {t("landing.haveAccount")}
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" strokeWidth={1.5} /> {t("landing.annualContract")}</span>
              <span className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-gold" strokeWidth={1.5} /> {t("landing.successFeeResult")}</span>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative overflow-hidden border border-sand shadow-[0_20px_60px_rgba(11,19,43,0.15)]">
              <img src={HERO} alt="Skyline Middle East" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-midnight/25" />
            </div>
            <div className="absolute -bottom-6 -start-6 hidden md:block bg-midnight text-bone p-6 max-w-[220px] border border-gold/30">
              <p className="text-xs mt-1 text-bone/80">{t("landing.badgeText")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-midnight text-bone py-20">
        <div className="pointer-events-none absolute inset-0">
          <div className="blob blob-teal animate-float" style={{ top: "-120px", left: "-60px", opacity: 0.3 }} />
          <div className="blob blob-gold animate-float" style={{ bottom: "-140px", right: "-40px", opacity: 0.25, animationDelay: "3s" }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="overline text-gold">{t("landing.howItWorks")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mt-3 tracking-tight">{t("landing.howTitle")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <div key={i} className="relative border border-bone/15 p-7 hover:-translate-y-2 transition-all duration-300 bg-white/[0.03] overflow-hidden group" style={{ borderTop: `3px solid ${accents[i]}` }} data-testid={`step-card-${i}`}>
                  <div className="absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ background: accents[i] }} />
                  <Icon className="relative h-7 w-7" strokeWidth={1.5} style={{ color: accents[i] }} />
                  <p className="overline text-bone/40 mt-5">{t("landing.stepLabel")} {i + 1}</p>
                  <h3 className="font-serif text-xl mt-2 text-bone">{s.title}</h3>
                  <p className="text-sm text-bone/60 mt-3 leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 cta-tint">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative overflow-hidden border border-sand order-2 lg:order-1 group">
            <div className="absolute inset-0 bg-gradient-to-tr from-gold/20 via-transparent to-teal-500/10 z-10 pointer-events-none" />
            <img src={MEETING} alt="Riyadh" className="w-full h-[360px] object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="overline text-gold mb-3">{t("landing.ctaOverline")}</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-midnight tracking-tight leading-tight">
              {t("landing.ctaTitle")}
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed max-w-lg">
              {t("landing.ctaText")}
            </p>
            <Link to="/register" className="inline-block mt-8" data-testid="cta-register-btn">
              <Button className="rounded-none h-12 px-7 bg-midnight text-bone hover:bg-midnight/90 hover:-translate-y-0.5 transition-all duration-300">
                {t("landing.ctaButton")} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-sand py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="font-serif text-lg text-midnight">Qafila</span>
          <span>© {new Date().getFullYear()} {t("landing.footer")}</span>
        </div>
      </footer>
    </div>
  );
}
